import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import applicationService from '../../services/applicationService';
import documentService from '../../services/documentService';
import consentService from '../../services/consentService';
import reportService from '../../services/reportService';
import LeaseSection from './LeaseSection';
import StatusBadge from '../common/StatusBadge';
import Alert from '../common/Alert';
import LoadingSpinner from '../common/LoadingSpinner';
import { formatDateTime } from '../../utils/formatters';
import { statusLabel } from '../../utils/applicationStatus';
import { ROUTES } from '../../utils/constants';
import './ApplicationDetail.css';

const messageFrom = (err, fallback) => err.response?.data?.message || err.message || fallback;

const CONSENT_LABELS = {
  identity: 'Identity verification',
  income: 'Income verification',
  credit: 'Credit check',
  employment_reference: 'Employer reference',
  landlord_reference: 'Previous landlord reference',
  reuse: 'Reuse of previous verification',
};

const ApplicationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('summary');
  const [application, setApplication] = useState(null);
  const [documents, setDocuments] = useState(null);
  const [consents, setConsents] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [notice, setNotice] = useState(null);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    setLoadError('');
    try {
      setApplication(await applicationService.get(id));
    } catch (err) {
      setLoadError(messageFrom(err, 'This application could not be loaded.'));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (activeTab === 'documents' && documents === null) {
      documentService.getDocuments(id).then(setDocuments).catch((err) => setNotice({ type: 'error', message: messageFrom(err, 'Documents could not be loaded.') }));
    }
    if (activeTab === 'consents' && consents === null) {
      consentService.getConsents(id).then(setConsents).catch((err) => setNotice({ type: 'error', message: messageFrom(err, 'Consent history could not be loaded.') }));
    }
  }, [activeTab, id, documents, consents]);

  const downloadDocument = async (docId, filename) => {
    try {
      const blob = await documentService.getDocument(docId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'document';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setNotice({ type: 'error', message: messageFrom(err, 'This document could not be opened.') });
    }
  };

  const generateReport = async () => {
    setGenerating(true);
    setNotice(null);
    try {
      await reportService.generateReport(id);
      setNotice({ type: 'success', message: 'Report generated.' });
      await load();
    } catch (err) {
      // e.g. verification isn't complete yet for this application
      setNotice({ type: 'error', message: messageFrom(err, 'The report could not be generated.') });
    } finally {
      setGenerating(false);
    }
  };

  if (loadError) return <Alert type="error" message={loadError} />;
  if (!application) return <LoadingSpinner fullPage />;

  const profile = application.applicant_profile;
  const applicantName = profile && (profile.first_name || profile.last_name)
    ? [profile.first_name, profile.last_name].filter(Boolean).join(' ')
    : 'Pending applicant';

  const renderSummary = () => (
    <div className="detail-section">
      <h3>Applicant Summary</h3>
      {profile ? (
        <dl className="info-grid">
          <div className="info-item"><dt>Name</dt><dd>{applicantName}</dd></div>
          <div className="info-item"><dt>ID Number</dt><dd>{profile.id_number || '—'}</dd></div>
          <div className="info-item"><dt>Gross Income</dt><dd>{profile.gross_monthly_income || '—'}</dd></div>
          <div className="info-item"><dt>Employment</dt><dd>{profile.employment_type || '—'}</dd></div>
          <div className="info-item"><dt>Household size</dt><dd>{profile.household_size ?? '—'}</dd></div>
          <div className="info-item"><dt>Current address</dt><dd>{profile.current_address || '—'}</dd></div>
        </dl>
      ) : (
        <p>The applicant has not started their profile yet.</p>
      )}
    </div>
  );

  const renderDocuments = () => (
    <div className="detail-section">
      <h3>Submitted Documents ({application.document_count ?? 0})</h3>
      {documents === null ? <LoadingSpinner /> : documents.length > 0 ? (
        <ul className="doc-list">
          {documents.map((doc) => (
            <li key={doc.id} className="doc-item">
              <span className="doc-type">{doc.document_type}</span>
              <span className="doc-name">{doc.original_filename}</span>
              <StatusBadge status={doc.status} />
              <button className="btn-action" onClick={() => downloadDocument(doc.id, doc.original_filename)}>View</button>
            </li>
          ))}
        </ul>
      ) : (
        <p>No documents uploaded yet.</p>
      )}
    </div>
  );

  const renderConsents = () => (
    <div className="detail-section">
      <h3>Consent History</h3>
      {consents === null ? <LoadingSpinner /> : consents.length > 0 ? (
        <div className="consent-summary-grid">
          {consents.map((c) => (
            <div key={c.id} className="consent-summary-item">
              <span>{CONSENT_LABELS[c.check_type] || c.check_type}</span>
              <StatusBadge status={c.withdrawn_at ? 'WITHDRAWN' : c.choice} />
            </div>
          ))}
        </div>
      ) : (
        <p>No consent has been recorded yet.</p>
      )}
    </div>
  );

  return (
    <div className="application-detail">
      <p><Link to={ROUTES.APPLICATIONS}>&larr; Applications</Link></p>

      <div className="application-detail__header card">
        <div className="card-body header-content">
          <div>
            <h1>Application for {application.property?.address || 'a property'}</h1>
            <p className="header-meta">
              Applicant: {applicantName} · Created: {formatDateTime(application.created_at)}
            </p>
          </div>
          <div className="header-status">
            <StatusBadge status={application.status} text={statusLabel(application.status)} />
          </div>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={generateReport} disabled={generating}>
            {generating ? 'Generating…' : 'Generate report'}
          </button>
          <button className="btn-secondary" onClick={() => navigate(`${ROUTES.APPLICATIONS}/${id}/report`)}>View report</button>
          <button className="btn-primary" onClick={() => navigate(`${ROUTES.APPLICATIONS}/${id}/decision`)}>Record decision</button>
          <button className="btn-secondary" onClick={() => navigate(`${ROUTES.APPLICATIONS}/${id}/disputes`)}>Disputes</button>
        </div>
      </div>

      {notice && <Alert type={notice.type} message={notice.message} dismissible onClose={() => setNotice(null)} />}

      <div className="application-detail__tabs">
        <button className={`tab-btn ${activeTab === 'summary' ? 'active' : ''}`} onClick={() => setActiveTab('summary')}>Profile</button>
        <button className={`tab-btn ${activeTab === 'documents' ? 'active' : ''}`} onClick={() => setActiveTab('documents')}>Documents</button>
        <button className={`tab-btn ${activeTab === 'consents' ? 'active' : ''}`} onClick={() => setActiveTab('consents')}>Consents</button>
        <button className={`tab-btn ${activeTab === 'lease' ? 'active' : ''}`} onClick={() => setActiveTab('lease')}>Lease</button>
      </div>

      <div className="application-detail__content card">
        <div className="card-body">
          {activeTab === 'summary' && renderSummary()}
          {activeTab === 'documents' && renderDocuments()}
          {activeTab === 'consents' && renderConsents()}
          {activeTab === 'lease' && <LeaseSection applicationId={id} />}
        </div>
      </div>
    </div>
  );
};

export default ApplicationDetail;
